import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { copy } from 'copy-anything'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'

import {
  AirportFlyDirection,
  SelectedAirportFlyDirection,
} from 'shared/models/ticket'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { BlockSelect } from 'shared/ui/block-select'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { HeaderResetButton } from 'shared/ui/header-reset-button'

import s from './styles.module.scss'

const getDefaultValue = (listAirports?: AirportFlyDirection[]) => {
  return listAirports?.map((item) => ({
    departure: item.departure.length === 1 ? [item.departure[0].code] : [],
    transfer: item.transfer.length === 1 ? [item.transfer[0].code] : [],
    arrival: item.arrival.length === 1 ? [item.arrival[0].code] : [],
  }))
}

const AirportsFilter = observer(() => {
  const navigate = useNavigate()

  const { t } = useTranslation()

  const listAirports = findTicketFormStore.dataForFilter?.airports
  const selectedAirports = findTicketFormStore.formFindTicket?.filter?.airports

  const [selected, setSelected] = useState<
    SelectedAirportFlyDirection[] | undefined
  >(() => {
    if (selectedAirports) {
      return selectedAirports
    }

    return getDefaultValue(listAirports)
  })

  const onChange = (data: string[], keyObj: string, index: number) => {
    const selectedCopy = copy(selected) || [{}]

    selectedCopy[index][keyObj as keyof SelectedAirportFlyDirection] = data

    setSelected(selectedCopy)
  }

  const onConfirmSelected = () => {
    const copyForm = copy(findTicketFormStore.formFindTicket.filter) || {}

    copyForm.airports = selected

    findTicketFormStore.onSetFilter(copyForm)

    navigate(RoutingPaths.avia.search.path())
  }

  const onBack = () => {
    navigate(-1)
  }

  const onResetFilter = () => {
    setSelected(getDefaultValue(listAirports))
  }

  return (
    <div className={s.container}>
      <div className={s.header}>
        <HeaderResetButton
          title={t('airports')}
          isCanDisable={true}
          onBack={onBack}
          onReset={onResetFilter}
        />
      </div>

      {listAirports?.map((airport, index) => {
        const departure = airport?.departure
        const transfer = airport?.transfer
        const arrival = airport?.arrival

        return (
          <div key={airport?.departure_city?.code}>
            <p className={s.name}>
              {airport?.departure_city?.title} – {airport?.arrival_city?.title}
            </p>

            <div className={s.block}>
              <BlockSelect
                title={t('departure')}
                active={selected?.[index]?.departure}
                onChange={
                  departure?.length > 1
                    ? (value) => onChange(value as string[], 'departure', index)
                    : () => {}
                }
                listOption={departure?.map((item) => ({
                  title: `${item?.title} (${item.code})`,
                  key: item.code,
                  prefix: (
                    <AppIcon
                      id={AllIcons.PLANE}
                      className={s.icon}
                      height={24}
                      width={24}
                    />
                  ),
                }))}
              />
            </div>

            <div className={s.block}>
              <BlockSelect
                title={t('transfer')}
                active={selected?.[index]?.transfer}
                onChange={
                  transfer?.length > 1
                    ? (value) => onChange(value as string[], 'transfer', index)
                    : () => {}
                }
                listOption={transfer?.map((item) => ({
                  title: `${item.title} (${item.code})`,
                  key: item.code,
                  prefix: (
                    <AppIcon
                      id={AllIcons.PLANE}
                      className={s.icon}
                      height={24}
                      width={24}
                    />
                  ),
                }))}
              />
            </div>

            <div className={s.block}>
              <BlockSelect
                title={t('arrive')}
                active={selected?.[index]?.arrival}
                onChange={
                  arrival?.length > 1
                    ? (value) => onChange(value as string[], 'arrival', index)
                    : () => {}
                }
                listOption={arrival?.map((item) => ({
                  title: `${item?.title} (${item.code})`,
                  key: item.code,
                  prefix: (
                    <AppIcon
                      id={AllIcons.PLANE}
                      className={s.icon}
                      height={24}
                      width={24}
                    />
                  ),
                }))}
              />
            </div>
          </div>
        )
      })}

      <ButtonContinue onClick={onConfirmSelected}>
        <span className={s.textButton}>{t('apply')}</span>
      </ButtonContinue>
    </div>
  )
})

export default AirportsFilter
